import React, { useState } from 'react';
import { Activity, CalendarClock, Home, Menu, Settings, Trophy } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import SportEmoji from '@/components/SportEmojis';

const navigationLinks = [
  { name: 'Home', path: '/', icon: Home, type: 'primary' },
  { name: 'Sports', path: '/sport/soccer', icon: Trophy, type: 'primary' },
  { name: 'Upcoming', path: '/upcoming', icon: CalendarClock, type: 'primary' },
  { name: 'My Replay Plan', path: '/plan', icon: CalendarClock, type: 'primary' },
  { name: 'Settings', path: '/settings', icon: Settings, type: 'primary' },
  { name: 'NBA', path: '/sport/nba', type: 'sport' },
  { name: 'Soccer', path: '/sport/soccer', type: 'sport' },
  { name: 'NFL', path: '/sport/nfl', type: 'sport' },
  { name: 'MLB', path: '/sport/mlb', type: 'sport' },
  { name: 'NHL', path: '/sport/nhl', type: 'sport' },
  { name: 'Blog', path: '/blog', type: 'secondary' },
  { name: 'Alternatives', path: '/spoiler-free-sports-alternatives', type: 'secondary' },
  { name: 'FAQ', path: '/faq', type: 'secondary' },
];

function isActive(pathname: string, target: string): boolean {
  return pathname === target || (target !== '/' && pathname.startsWith(target));
}

const Header: React.FC = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const desktopLinks = navigationLinks.filter(({ type, name }) => type === 'sport' || type === 'secondary' || name === 'Home');

  return (
    <header className="sticky top-0 z-40 border-b border-indigo-400/20 bg-indigo-700 shadow-md [padding-top:env(safe-area-inset-top)]">
      <div className="container mx-auto flex min-h-14 items-center justify-between px-4 md:min-h-20 md:px-6">
        <Link to="/" className="flex min-h-11 items-center gap-2 rounded-lg text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
          <Activity className="h-5 w-5 md:h-7 md:w-7" strokeWidth={1.7} aria-hidden="true" />
          <span className="text-xl font-bold md:text-3xl">skore<span className="text-indigo-200">.info</span></span>
        </Link>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white md:hidden"
              aria-label="Open navigation"
            >
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </SheetTrigger>
          <SheetContent className="w-[min(88vw,22rem)] border-slate-700 bg-slate-950 px-5 text-slate-100 [padding-top:max(1.5rem,env(safe-area-inset-top))]">
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl text-white">Where do you want to go?</SheetTitle>
              <SheetDescription className="text-slate-400">Scores stay hidden until you choose to reveal them.</SheetDescription>
            </SheetHeader>

            <nav aria-label="Primary" className="mt-7">
              <ul className="space-y-1" role="list">
                {navigationLinks.filter(({ type }) => type === 'primary').map(({ name, path, icon: Icon }) => (
                  <li key={path}>
                    <SheetClose asChild>
                      <Link to={path} className={`flex min-h-12 items-center gap-3 rounded-xl px-3 text-base font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-300 ${isActive(pathname, path) ? 'bg-indigo-500/25 text-indigo-100' : 'text-slate-200 hover:bg-white/8 hover:text-white'}`}>
                        {Icon && <Icon className="h-5 w-5 text-indigo-300" aria-hidden="true" />}
                        {name}
                      </Link>
                    </SheetClose>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="mt-7 border-t border-white/10 pt-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Sports</p>
              <div className="grid grid-cols-2 gap-2">
                {navigationLinks.filter(({ type }) => type === 'sport').map(({ name, path }) => (
                  <SheetClose asChild key={path}>
                    <Link to={path} className={`flex min-h-14 items-center gap-2 rounded-lg px-3 text-sm font-medium ${isActive(pathname, path) ? 'bg-indigo-500/25 text-indigo-100' : 'bg-white/[0.04] text-slate-300 hover:bg-white/10'}`}>
                      <SportEmoji sportType={name} size="small" />
                      <span>{name}</span>
                    </Link>
                  </SheetClose>
                ))}
              </div>
            </div>

            <nav aria-label="More" className="mt-7 border-t border-white/10 pt-5">
              <ul className="space-y-1" role="list">
                {navigationLinks.filter(({ type }) => type === 'secondary').map(({ name, path }) => (
                  <li key={path}><SheetClose asChild><Link to={path} className="flex min-h-11 items-center rounded-lg px-3 text-sm text-slate-400 hover:bg-white/8 hover:text-white">{name}</Link></SheetClose></li>
                ))}
              </ul>
            </nav>
          </SheetContent>
        </Sheet>

        <nav aria-label="Primary" className="hidden md:block">
          <ul className="flex items-center gap-1" role="list">
            {desktopLinks.map(({ name, path }) => (
              <li key={path}>
                <Link to={path} className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${isActive(pathname, path) ? 'bg-indigo-800 text-white' : 'text-indigo-100 hover:bg-indigo-600 hover:text-white'}`}>{name}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
